import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import Messages from './Messages'
import InjuryExplorer from './InjuryExplorer'
import PatientBilling from './PatientBilling'
import LanguageSwitcher from './LanguageSwitcher'
import { useLanguage } from '../context/LanguageContext'
import { useCaleb } from '../context/CalebContext'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const C = {
  teal:       '#0d9488',
  tealDark:   '#0f766e',
  tealDeep:   '#065f46',
  tealMid:    '#99f6e4',
  tealLight:  '#f0fdfa',
  tealFoam:   '#ccfbf1',
  purple:     '#7c3aed',
  purpleDark: '#6d28d9',
  purpleMid:  '#c4b5fd',
  purpleLight:'#f5f3ff',
  white:      '#ffffff',
  ink:        '#0f172a',
  inkMid:     '#334155',
  inkLt:      '#64748b',
  border:     '#e2e8f0',
  success:    '#16a34a',
  warn:       '#d97706',
  red:        '#dc2626',
  amber:      '#fef3c7',
  cream:      '#f8fafc',
}

const DISPLAY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO    = "'DM Mono', 'Courier New', monospace"

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes fadeUp   { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin     { to { transform: rotate(360deg); } }
  @keyframes modalIn  { from { opacity:0; transform:scale(.96) translateY(12px); } to { opacity:1; transform:scale(1) translateY(0); } }
  @keyframes popIn    { 0%{transform:scale(.7);opacity:0} 70%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
  @keyframes confetti { 0%{transform:translateY(0) rotate(0);opacity:1} 100%{transform:translateY(-60px) rotate(180deg);opacity:0} }
  @keyframes strokeDraw { from{stroke-dashoffset:var(--full)} to{stroke-dashoffset:var(--offset)} }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.55} }
  @keyframes shimmer  { from{background-position:200% 0} to{background-position:-200% 0} }

  .pv-page { animation: fadeUp .45s cubic-bezier(.22,1,.36,1) both; }

  .nav-item {
    width: 100%; display: flex; align-items: center; gap: 11px;
    padding: 11px 14px; border-radius: 10px; border: none;
    background: transparent; cursor: pointer;
    font-family: ${BODY}; font-size: 14px; font-weight: 500; text-align: left;
    transition: all .15s; color: rgba(255,255,255,0.5);
  }
  .nav-item:hover  { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.85); }
  .nav-item.active { background: rgba(255,255,255,0.14); color: white; font-weight: 600; }

  .lift { transition: transform .2s ease; position: relative; overflow: hidden; }
  .lift::after { content: ''; position: absolute; inset: 0; border-radius: inherit; padding: 1.5px; background: linear-gradient(135deg, #0d9488, #7c3aed); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; z-index: 1; }
  .lift:hover { transform: translateY(-2px); }

  .btn-teal { transition: all .15s; }
  .btn-teal:hover { background: ${C.tealDark} !important; transform: translateY(-1px); }

  .diff-btn {
    flex: 1; padding: 14px 0; border-radius: 10px;
    border: 1.5px solid ${C.border}; background: white;
    cursor: pointer; font-size: 16px; font-weight: 600;
    color: ${C.inkLt}; font-family: ${BODY}; transition: all .12s;
  }
  .diff-btn.sel { border-color: ${C.purple} !important; background: ${C.purpleLight} !important; color: ${C.purpleDark} !important; }
  .diff-btn:hover:not(.sel) { border-color: ${C.purpleMid}; }

  .log-btn { transition: all .15s; }
  .log-btn:hover { background: ${C.purpleDark} !important; }

  .quick-card { transition: transform .18s; cursor: pointer; position: relative; overflow: hidden; }
  .quick-card::after { content: ''; position: absolute; inset: 0; border-radius: inherit; padding: 1.5px; background: linear-gradient(135deg, #0d9488, #7c3aed); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; z-index: 1; }
  .quick-card:hover { transform: translateY(-2px); }

  .no-scroll::-webkit-scrollbar { display: none; }

  .ring-arc { animation: strokeDraw 1.2s cubic-bezier(.22,1,.36,1) .2s both; }
  .badge-pop { animation: popIn .4s cubic-bezier(.22,1,.36,1) both; }
  .streak-glow { box-shadow: 0 0 0 3px rgba(13,148,136,.18); }

  @media (min-width: 960px) {
    .pv-shell   { display: flex !important; align-items: stretch !important; min-height: 100vh !important; }
    .pv-sidebar { display: flex !important; width: 260px !important; flex-shrink: 0 !important;
                  position: sticky !important; top: 0 !important; height: 100vh !important;
                  overflow-y: auto !important; z-index: 500 !important; }
    .pv-topbar  { display: none !important; }
    .mob-tabs   { display: none !important; }
    .pv-content { padding: 52px 60px 100px !important; max-width: 820px; margin: 0 auto; }
    .grid-2 { display: grid !important; grid-template-columns: 1fr 1fr; gap: 16px !important; }
  }
  @media (max-width: 959px) {
    .pv-sidebar { display: none !important; }
    .pv-topbar  { display: flex !important; z-index: 600 !important; }
    .mob-tabs   { display: flex !important; overflow: hidden !important; z-index: 590 !important; }
    .pv-content { padding: 24px 16px 100px !important; }
    /* Tab bar: fill width evenly, icon-only on small screens */
    .mob-tab-btn { flex: 1; min-width: 0; padding: 10px 3px !important; flex-direction: column !important; gap: 3px !important; font-size: 10px !important; justify-content: center; white-space: nowrap; overflow: hidden; }
    .mob-tab-label { display: none !important; }
  }
  @media (min-width: 600px) and (max-width: 959px) {
    .mob-tab-btn { font-size: 11px !important; padding: 10px 4px !important; flex-direction: row !important; }
    .mob-tab-label { display: inline !important; overflow: hidden; text-overflow: ellipsis; max-width: 60px; }
  }
`

const inputSt = {
  width: '100%', padding: '12px 16px', fontSize: 16,
  border: `1.5px solid rgba(13,148,136,0.25)`, borderRadius: 12,
  color: C.ink, background: C.white, fontFamily: BODY, outline: 'none', display: 'block',
}

const labelSt = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: C.inkLt, textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: 7,
}

function Icon({ d, size = 18, color = 'currentColor', sw = 1.7 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {d.split('|||').map((p, i) => <path key={i} d={p} />)}
    </svg>
  )
}

const IC = {
  home:     'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z|||M9 22V12h6v10',
  heart:    'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  exercise: 'M6.5 6.5h11|||M6.5 17.5h11|||M3 12h18',
  target:   'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z|||M12 18a6 6 0 100-12 6 6 0 000 12z|||M12 14a2 2 0 100-4 2 2 0 000 4z',
  msg:      'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  cal:      'M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z|||M16 2v4|||M8 2v4|||M3 10h18',
  body:     'M12 2a3 3 0 110 6 3 3 0 010-6z|||M8 8h8l1 4H7L8 8z|||M10 12l-1 10h6l-1-10',
  out:      'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4|||M16 17l5-5-5-5|||M21 12H9',
  check:    'M20 6L9 17l-5-5',
  trend:    'M23 6l-9.5 9.5-5-5L1 18',
  arrow:    'M5 12h14|||M12 5l7 7-7 7',
  x:        'M18 6L6 18|||M6 6l12 12',
  play:     'M5 3l14 9-14 9V3z',
  fire:     'M12 2c0 0-6 6-6 12a6 6 0 0012 0c0-6-6-12-6-12z|||M12 14a2 2 0 010 4',
  star:     'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  trophy:   'M6 9H4.5a2.5 2.5 0 010-5H6|||M18 9h1.5a2.5 2.5 0 000-5H18|||M4 22h16|||M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22|||M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22|||M18 2H6v7a6 6 0 0012 0V2z',
  user:     'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2|||M12 11a4 4 0 100-8 4 4 0 000 8z',
  bill:     'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z|||M14 2v6h6|||M16 13H8|||M16 17H8|||M10 9H8',
}

/*  Recovery Ring  */
function RecoveryRing({ pct, sessionsLeft, isProgressing, sessionsDone, totalSessions }) {
  const R = 88
  const CIRC = 2 * Math.PI * R
  const offset = CIRC - (pct / 100) * CIRC
  // Purple → green gradient: low = purple, mid = violet, high = emerald
  const color  = pct < 33 ? C.purple : pct < 66 ? '#8b5cf6' : '#10b981'

  const milestones = [25, 50, 75, 100]
  const nextMilestone = milestones.find(m => m > pct) || 100
  const sessionsToNext = Math.ceil(((nextMilestone - pct) / 100) * totalSessions)

  return (
    <div style={{ background: 'white', borderRadius: 24, padding: '28px 24px', border: `1.5px solid rgba(13,148,136,0.25)`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      {/* Ring */}
      <div style={{ position: 'relative', width: 210, height: 210 }}>
        <svg width="210" height="210" viewBox="0 0 210 210" style={{ transform: 'rotate(-90deg)' }}>
          <defs>
            {/* Gradient runs left→right across the circle bounding box */}
            <linearGradient id="ring-progress-grad" x1="17" y1="105" x2="193" y2="105" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor={C.purple}  />
              <stop offset="50%"  stopColor="#8b5cf6"   />
              <stop offset="100%" stopColor="#10b981"   />
            </linearGradient>
          </defs>
          {/* Track */}
          <circle cx="105" cy="105" r={R} fill="none" stroke={C.purpleLight} strokeWidth="14" />
          {/* Progress arc — always uses gradient */}
          <circle
            cx="105" cy="105" r={R}
            fill="none"
            stroke="url(#ring-progress-grad)"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={offset}
            className="ring-arc"
            style={{ '--full': CIRC, '--offset': offset }}
          />
          {/* Milestone ticks */}
          {milestones.map(m => {
            const angle = (m / 100) * 2 * Math.PI - Math.PI / 2
            const x1 = 105 + (R - 8) * Math.cos(angle)
            const y1 = 105 + (R - 8) * Math.sin(angle)
            const x2 = 105 + (R + 8) * Math.cos(angle)
            const y2 = 105 + (R + 8) * Math.sin(angle)
            return (
              <line key={m} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={pct >= m ? color : C.border}
                strokeWidth="2.5" strokeLinecap="round" />
            )
          })}
        </svg>

        {/* Center text */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 48, color: color, lineHeight: 1, letterSpacing: '-0.02em' }}>{pct}%</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: 4 }}>recovered</div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 5, background: isProgressing ? C.purpleLight : '#fef3c7', padding: '4px 12px', borderRadius: 99, border: `1px solid ${isProgressing ? C.purpleMid : '#fde68a'}` }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: isProgressing ? C.purple : C.warn, flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: isProgressing ? C.purpleDark : '#92400e', letterSpacing: '0.04em' }}>
              {isProgressing ? 'On track' : 'Plateau'}
            </span>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: C.border, borderRadius: 14, overflow: 'hidden', width: '100%' }}>
        {[
          { label: 'Sessions done', value: sessionsDone },
          { label: 'Remaining', value: sessionsLeft ?? '—' },
          { label: 'Next milestone', value: `${nextMilestone}%` },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', padding: '14px 10px', textAlign: 'center' }}>
            <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 24, color: C.ink, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Next milestone nudge */}
      {pct < 100 && (
        <div style={{ width: '100%', background: C.purpleLight, borderRadius: 14, padding: '14px 18px', border: `1px solid ${C.purpleMid}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg, ${C.purple}, #10b981)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon d={IC.target} size={16} color="white" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.purpleDark, lineHeight: 1.3 }}>
              {sessionsToNext === 1 ? 'One more session' : `~${sessionsToNext} more sessions`} to {nextMilestone}%
            </div>
            <div style={{ fontSize: 12, color: C.inkLt, marginTop: 2 }}>Keep showing up — you're making real progress.</div>
          </div>
        </div>
      )}
    </div>
  )
}

/*  Streak Badge  */
function StreakBadge({ exerciseLogs }) {
  const today = new Date()
  let streak = 0
  for (let i = 0; i < 60; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const ds = d.toISOString().split('T')[0]
    if (exerciseLogs.some(l => l.log_date === ds)) streak++
    else break
  }

  if (streak === 0) return null

  const isHot    = streak >= 7
  const isMedium = streak >= 3

  return (
    <div className="badge-pop" style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: isHot ? `linear-gradient(135deg, ${C.tealDark} 0%, ${C.purpleDark} 100%)` : C.purpleLight,
      borderRadius: 14, padding: '14px 18px',
      border: `1.5px solid ${isHot ? 'transparent' : C.purpleMid}`,
    }}>
      <div style={{ fontSize: 28, flexShrink: 0 }}>
        {isHot ? '!' : isMedium ? '~' : ''}
      </div>
      <div>
        <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 22, color: isHot ? 'white' : C.ink, lineHeight: 1 }}>
          {streak}-day streak
        </div>
        <div style={{ fontSize: 13, color: isHot ? 'rgba(255,255,255,0.65)' : C.inkLt, marginTop: 3 }}>
          {isHot ? "You're on fire — don't break it!" : isMedium ? 'Building great momentum.' : 'Great start — keep going!'}
        </div>
      </div>
    </div>
  )
}

/*  Daily Exercise Progress  */
function TodayProgress({ loggedCount, total }) {
  const allDone = loggedCount === total && total > 0
  return (
    <div style={{
      background: allDone ? `linear-gradient(135deg, ${C.tealDark} 0%, ${C.purpleDark} 100%)` : `linear-gradient(135deg, ${C.tealDark} 0%, #1e1b4b 100%)`,
      borderRadius: 20, padding: '22px 24px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>Today's exercises</p>
        <span style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 30, color: 'white' }}>
          {loggedCount}<span style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }}>/{total}</span>
        </span>
      </div>
      <div style={{ height: 8, background: 'rgba(255,255,255,0.12)', borderRadius: 99, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{
          height: '100%',
          width: `${total > 0 ? (loggedCount / total) * 100 : 0}%`,
          background: allDone ? '#34d399' : `linear-gradient(90deg, ${C.tealMid}, #34d399)`,
          borderRadius: 99,
          transition: 'width .7s cubic-bezier(.22,1,.36,1)',
        }} />
      </div>
      {allDone
        ? <p style={{ fontSize: 14, color: '#6ee7b7', fontWeight: 700 }}>All done for today — excellent work!</p>
        : total > 0 && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            {total - loggedCount} remaining · Every rep counts.
          </p>
      }
    </div>
  )
}

export default function PatientView({ user, onLogout }) {
  const { open: openCaleb } = useCaleb()
  const { t } = useLanguage()

  const [patient,       setPatient]       = useState(null)
  const [progress,      setProgress]      = useState(null)
  const [goals,         setGoals]         = useState([])
  const [exercises,     setExercises]     = useState([])
  const [exerciseLogs,  setExerciseLogs]  = useState([])
  const [appointments,  setAppointments]  = useState([])
  const [apptForm,      setApptForm]      = useState({ appointment_date: '', appointment_time: '', notes: '' })
  const [apptMessage,   setApptMessage]   = useState('')
  const [activeTab,     setActiveTab]     = useState('Home')
  const [logModal,      setLogModal]      = useState(null)
  const [logForm,       setLogForm]       = useState({ difficulty: 3, notes: '' })
  const [logSubmitting, setLogSubmitting] = useState(false)
  // Password change
  const [pwForm,        setPwForm]        = useState({ current: '', next: '', confirm: '' })
  const [pwMsg,         setPwMsg]         = useState(null)  // { ok: bool, text: str }
  const [pwSaving,      setPwSaving]      = useState(false)
  // Discharge survey
  const [surveyRating,  setSurveyRating]  = useState(0)
  const [surveyComment, setSurveyComment] = useState('')
  const [surveyDone,    setSurveyDone]    = useState(false)
  const [surveySaving,  setSurveySaving]  = useState(false)

  useEffect(() => { fetchAll() }, [])

  const fetchAll = () => {
    const id = user.patient_id
    axios.get(`${API}/patients/${id}`).then(r => setPatient(r.data)).catch(() => {})
    axios.get(`${API}/patients/${id}/progress`).then(r => setProgress(r.data)).catch(() => {})
    axios.get(`${API}/patients/${id}/goals`).then(r => setGoals(r.data)).catch(() => {})
    axios.get(`${API}/patients/${id}/exercises`).then(r => setExercises(r.data)).catch(() => {})
    axios.get(`${API}/patients/${id}/exercise-logs`).then(r => setExerciseLogs(r.data)).catch(() => {})
    axios.get(`${API}/patients/${id}/appointments`).then(r => setAppointments(r.data)).catch(() => {})
  }

  const today         = new Date().toISOString().split('T')[0]
  const isLoggedToday = id => exerciseLogs.some(l => l.exercise_id === id && l.log_date === today)

  const handleLogSubmit = () => {
    if (!logModal) return
    setLogSubmitting(true)
    axios.post(`${API}/exercise-logs`, {
      exercise_id: logModal.id, patient_id: user.patient_id,
      log_date: today, difficulty: logForm.difficulty, notes: logForm.notes,
    })
      .then(() => {
        setLogModal(null)
        setLogForm({ difficulty: 3, notes: '' })
        axios.get(`${API}/patients/${user.patient_id}/exercise-logs`).then(r => setExerciseLogs(r.data)).catch(() => {})
      })
      .catch(err => alert(err.response?.data?.detail || 'Failed to log exercise.'))
      .finally(() => setLogSubmitting(false))
  }

  const handlePasswordChange = async () => {
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) {
      setPwMsg({ ok: false, text: 'Please fill in all fields.' }); return
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwMsg({ ok: false, text: 'New passwords do not match.' }); return
    }
    if (pwForm.next.length < 8) {
      setPwMsg({ ok: false, text: 'New password must be at least 8 characters.' }); return
    }
    setPwSaving(true); setPwMsg(null)
    try {
      await axios.post(`${API}/auth/change-password`, {
        user_id: user.id,
        current_password: pwForm.current,
        new_password: pwForm.next,
      })
      setPwMsg({ ok: true, text: 'Password updated successfully!' })
      setPwForm({ current: '', next: '', confirm: '' })
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.detail || 'Failed to update password.' })
    } finally {
      setPwSaving(false)
    }
  }

  const handleSurveySubmit = async () => {
    if (surveyRating < 1) {
      alert('Please select a star rating.'); return
    }
    setSurveySaving(true)
    try {
      await axios.post(`${API}/patients/${user.patient_id}/discharge-survey`, {
        rating: surveyRating,
        comment: surveyComment,
      })
      setSurveyDone(true)
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit survey.')
    } finally {
      setSurveySaving(false)
    }
  }

  if (!patient) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(152deg, ${C.tealDark} 0%, #065f46 40%, #1e1b4b 100%)`, fontFamily: BODY }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.15)', borderTop: '2px solid rgba(153,246,228,0.8)', margin: '0 auto 20px', animation: 'spin .8s linear infinite' }} />
        <p style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 18, color: 'rgba(255,255,255,0.6)' }}>Loading your portal…</p>
      </div>
    </div>
  )

  const sessions       = progress?.sessions || []
  const prediction     = progress?.prediction
  const totalSessions  = prediction ? prediction.predicted_sessions_remaining + sessions.length : 20
  const recoveryPct    = sessions.length > 0 ? Math.min(Math.round((sessions.length / totalSessions) * 100), 99) : 0
  const latest         = sessions[sessions.length - 1]
  const first          = sessions[0]
  const painReduced    = first && latest ? Math.max(0, Math.round(((first.pain_score - latest.pain_score) / first.pain_score) * 100)) : 0
  const isProgressing  = prediction && !prediction.plateau_flag
  const completedGoals = goals.filter(g => g.is_complete).length
  const chartData      = sessions.map(s => ({ s: `S${s.session_number}`, Pain: s.pain_score }))
  const weekGroups     = exercises.reduce((acc, ex) => {
    const w = `Week ${ex.week_number}`; if (!acc[w]) acc[w] = []; acc[w].push(ex); return acc
  }, {})
  const confirmedAppts   = appointments.filter(a => a.status === 'confirmed')
  const loggedTodayCount = exercises.filter(ex => isLoggedToday(ex.id)).length
  const totalExercises   = exercises.length
  const initials         = patient.name.split(' ').map(n => n[0]).join('').slice(0, 2)

  const TABS = [
    { id: 'Home',             icon: IC.home,     label: t('nav_home')            },
    { id: 'My Health',        icon: IC.heart,    label: t('nav_my_health')       },
    { id: 'Exercises',        icon: IC.exercise, label: t('nav_exercises')       },
    { id: 'Goals',            icon: IC.target,   label: t('nav_goals')           },
    { id: 'Injury Explorer',  icon: IC.body,     label: t('nav_injury_explorer') },
    { id: 'Messages',         icon: IC.msg,      label: t('nav_messages')        },
    { id: 'Appointments',     icon: IC.cal,      label: t('nav_appointments')    },
    { id: 'Bills',            icon: IC.bill,     label: t('nav_bills')           },
    { id: 'Account',          icon: IC.user,     label: t('nav_account')         },
  ]

  return (
    <div style={{ fontFamily: BODY, background: C.cream, minHeight: '100vh' }}>
      <style>{GLOBAL_CSS}</style>

      {/* LOG MODAL */}
      {logModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,118,110,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(8px)' }}>
          <div style={{ background: C.white, borderRadius: 28, padding: '40px', width: '100%', maxWidth: 460, animation: 'modalIn .3s cubic-bezier(.22,1,.36,1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: C.purple, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Log Exercise</p>
                <h3 style={{ fontFamily: DISPLAY, fontSize: 26, color: C.ink, fontWeight: 400 }}>{logModal.exercise_name}</h3>
                <p style={{ fontSize: 14, color: C.inkLt, marginTop: 4, fontFamily: MONO }}>{logModal.sets} sets × {logModal.reps} reps</p>
              </div>
              <button onClick={() => { setLogModal(null); setLogForm({ difficulty: 3, notes: '' }) }}
                style={{ background: C.cream, border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon d={IC.x} size={14} color={C.inkLt} />
              </button>
            </div>
            <div style={{ height: 1, background: C.border, marginBottom: 28 }} />
            <div style={{ marginBottom: 24 }}>
              <label style={labelSt}>How difficult was it?</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setLogForm(f => ({ ...f, difficulty: n }))}
                    className={`diff-btn${logForm.difficulty === n ? ' sel' : ''}`}>{n}</button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: C.inkLt }}>Easy</span>
                <span style={{ fontSize: 12, color: C.inkLt }}>Very hard</span>
              </div>
            </div>
            <div style={{ marginBottom: 32 }}>
              <label style={labelSt}>Notes (optional)</label>
              <input className="pv-input" placeholder="e.g. Felt tightness on right side…"
                value={logForm.notes} onChange={e => setLogForm(f => ({ ...f, notes: e.target.value }))}
                style={inputSt} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setLogModal(null); setLogForm({ difficulty: 3, notes: '' }) }}
                style={{ flex: 1, padding: '14px', border: `1.5px solid rgba(13,148,136,0.25)`, borderRadius: 12, background: 'white', cursor: 'pointer', fontSize: 15, fontWeight: 600, fontFamily: BODY, color: C.inkLt }}>
                Cancel
              </button>
              <button onClick={handleLogSubmit} disabled={logSubmitting} className="btn-teal"
                style={{ flex: 2, padding: '14px', background: C.teal, color: 'white', border: 'none', borderRadius: 12, cursor: logSubmitting ? 'default' : 'pointer', fontSize: 15, fontWeight: 700, fontFamily: BODY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: logSubmitting ? .7 : 1 }}>
                {logSubmitting ? 'Saving…' : <><Icon d={IC.check} size={15} color="white" /> Mark Complete</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pv-shell" style={{ display: 'block', minHeight: '100vh' }}>
        {/* SIDEBAR */}
        <aside className="pv-sidebar" style={{ display: 'none', width: 260, flexShrink: 0, flexDirection: 'column', background: `linear-gradient(180deg, ${C.tealDeep} 0%, #1e1b4b 60%, #0a1f16 100%)`, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
          <div style={{ padding: '28px 22px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                  <path d="M16 7C16 7 10 11 10 17c0 3.3 2.7 6 6 6s6-2.7 6-6c0-6-6-10-6-10Z" stroke="white" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                  <line x1="16" y1="23" x2="16" y2="26" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="13" y1="25" x2="19" y2="25" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="13" y1="17" x2="19" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="16" y1="14" x2="16" y2="20" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <div style={{ fontFamily: DISPLAY, fontSize: 15, color: 'white', letterSpacing: '-0.01em', lineHeight: 1.1 }}>ClearPath PT</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Patient Portal</div>
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 14, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'white', flexShrink: 0 }}>{initials}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'white', letterSpacing: '-0.01em' }}>{patient.name}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>Patient</div>
                </div>
              </div>
            </div>
          </div>

          <nav style={{ flex: 1, padding: '4px 12px' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.22)', textTransform: 'uppercase', letterSpacing: '0.12em', padding: '0 2px 10px' }}>Navigation</p>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`nav-item${activeTab === tab.id ? ' active' : ''}`}>
                <Icon d={tab.icon} size={15} color={activeTab === tab.id ? 'white' : 'rgba(255,255,255,0.38)'} />
                {tab.label}
              </button>
            ))}
          </nav>

          <div style={{ padding: '20px 22px 28px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {/* Language Switcher */}
            <div style={{ marginBottom: 14 }}>
              <LanguageSwitcher variant="light" dropUp={true} style={{ width: '100%' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.32)', textTransform: 'uppercase', letterSpacing: '0.09em' }}>Recovery</p>
                <span style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 24, color: 'white' }}>{recoveryPct}%</span>
              </div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${recoveryPct}%`, background: `linear-gradient(90deg, ${C.teal}, ${C.purple})`, borderRadius: 99, transition: 'width .9s cubic-bezier(.22,1,.36,1)' }} />
              </div>
            </div>
            <button onClick={onLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', padding: '10px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: BODY, transition: 'all .15s' }}>
              <Icon d={IC.out} size={13} color="rgba(255,255,255,0.4)" />
              {t('btn_logout')}
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Mobile topbar */}
          <div className="pv-topbar" style={{ display: 'none', background: `linear-gradient(135deg, ${C.tealDeep}, ${C.purpleDark})`, padding: '0 16px', height: 58, justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white' }}>{initials}</div>
              <div>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'white', fontFamily: DISPLAY }}>{patient.name.split(' ')[0]}</span>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Patient Portal</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LanguageSwitcher variant="light" />
              <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', padding: '7px 13px', borderRadius: 99, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: BODY }}>
                <Icon d={IC.out} size={12} color="rgba(255,255,255,0.8)" /> {t('btn_logout')}
              </button>
            </div>
          </div>

          {/* Mobile tab bar — fills width without scrollbar; icon-only on phones */}
          <div className="mob-tabs" style={{ display: 'none', background: C.white, borderBottom: `1.5px solid rgba(13,148,136,0.2)`, position: 'sticky', top: 58, zIndex: 40 }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="mob-tab-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: activeTab === tab.id ? 700 : 500, border: 'none', borderBottom: `2.5px solid ${activeTab === tab.id ? C.teal : 'transparent'}`, background: 'none', cursor: 'pointer', color: activeTab === tab.id ? C.teal : C.inkLt, fontFamily: BODY }}>
                <Icon d={tab.icon} size={15} color={activeTab === tab.id ? C.teal : C.inkLt} />
                <span className="mob-tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Injury Explorer — full-width, outside pv-content constraint.
              Wrapped in a relative z-1 container so its fixed Background
              (z:0) stays visually below the sticky topbar/tabs (z:590-600). */}
          {activeTab === 'Injury Explorer' && (
            <div style={{ position: 'relative', zIndex: 1 }}>
              <InjuryExplorer patientId={user.patient_id} patientName={patient?.name} />
            </div>
          )}

          {/* PAGE CONTENT */}
          <div className="pv-content" style={{ padding: '24px 16px 100px', display: activeTab === 'Injury Explorer' ? 'none' : undefined }}>

            {/* HOME */}
            {activeTab === 'Home' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('home_welcome_back')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 36, fontWeight: 400, color: C.ink, lineHeight: 1.12, letterSpacing: '-0.02em' }}>
                    {patient.name.split(' ')[0]}'s<br /><em>{t('home_recovery_portal')}</em>
                  </h1>
                </div>

                {/* Streak badge */}
                <StreakBadge exerciseLogs={exerciseLogs} />

                {/* Recovery ring */}
                <RecoveryRing
                  pct={recoveryPct}
                  sessionsLeft={prediction?.predicted_sessions_remaining}
                  isProgressing={isProgressing}
                  sessionsDone={sessions.length}
                  totalSessions={totalSessions}
                />

                {/* Today's exercises */}
                {totalExercises > 0 && (
                  <TodayProgress loggedCount={loggedTodayCount} total={totalExercises} />
                )}

                {/* Next appointment banner */}
                {confirmedAppts.length > 0 && (
                  <div style={{ background: `linear-gradient(135deg, ${C.tealDeep}, ${C.tealDark})`, borderRadius: 20, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>{t('home_next_appt')}</p>
                      <p style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 20, color: 'white' }}>{confirmedAppts[0].appointment_date} at {confirmedAppts[0].appointment_time}</p>
                    </div>
                    <div style={{ background: 'rgba(153,246,228,.18)', color: C.tealMid, fontSize: 11, fontWeight: 700, padding: '5px 14px', borderRadius: 99, border: '1px solid rgba(153,246,228,.28)', whiteSpace: 'nowrap' }}>{t('home_confirmed')}</div>
                  </div>
                )}

                {/* Quick links */}
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>{t('home_quick_access')}</p>
                  <div className="grid-2" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { label: t('nav_messages'),     sub: t('home_chat_therapist'),                                                                        tab: 'Messages'     },
                      { label: t('nav_exercises'),     sub: `${loggedTodayCount}/${totalExercises} ${t('home_done_today')}`,                                 tab: 'Exercises'    },
                      { label: t('nav_goals'),         sub: `${completedGoals} of ${goals.length} ${t('home_of_completed')}`,                               tab: 'Goals'        },
                      { label: t('nav_appointments'),  sub: confirmedAppts[0] ? `Next: ${confirmedAppts[0].appointment_date}` : t('home_request_session'),   tab: 'Appointments' },
                    ].map(item => (
                      <button key={item.label} onClick={() => setActiveTab(item.tab)}
                        className="quick-card"
                        style={{ background: C.white, border: `1.5px solid rgba(13,148,136,0.25)`, borderRadius: 16, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: BODY }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{item.label}</div>
                          <div style={{ fontSize: 13, color: C.inkLt }}>{item.sub}</div>
                        </div>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: C.tealLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${C.tealMid}` }}>
                          <Icon d={IC.arrow} size={14} color={C.teal} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Latest session note */}
                {sessions.filter(s => s.notes || s.soap_subjective).length > 0 && (
                  <div style={{ background: C.white, borderRadius: 20, padding: '24px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>{t('home_latest_session')}</p>
                    {sessions.slice().reverse().filter(s => s.notes || s.soap_subjective).slice(0, 1).map(s => (
                      <div key={s.id}>
                        <p style={{ fontSize: 12, color: C.inkLt, marginBottom: 10, fontFamily: MONO }}>Session {s.session_number} · {s.date}</p>
                        <p style={{ fontSize: 16, color: C.inkMid, lineHeight: 1.8, fontFamily: DISPLAY, fontStyle: 'italic' }}>{s.notes || s.soap_subjective}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Discharge survey */}
                {patient.discharge_date && (
                  <div style={{ background: C.white, borderRadius: 20, padding: '28px', border: `1.5px solid ${C.tealMid}`, boxShadow: `0 0 0 4px ${C.tealLight}` }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('home_care_complete')}</p>
                    <h3 style={{ fontFamily: DISPLAY, fontSize: 22, color: C.ink, fontWeight: 400, marginBottom: 6 }}>{t('home_how_experience')}</h3>
                    <p style={{ fontSize: 14, color: C.inkLt, marginBottom: 20, lineHeight: 1.6 }}>{t('home_discharged_congrats')}</p>
                    {surveyDone ? (
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}></div>
                        <p style={{ fontFamily: DISPLAY, fontSize: 20, color: C.tealDark, fontStyle: 'italic' }}>{t('home_thanks_feedback')}</p>
                        <p style={{ fontSize: 14, color: C.inkLt, marginTop: 6 }}>{t('home_helps_improving')}</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: 20 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: C.inkMid, marginBottom: 10 }}>{t('home_overall_rating')}</p>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {[1,2,3,4,5].map(n => (
                              <button key={n} onClick={() => setSurveyRating(n)}
                                style={{ fontSize: 28, background: 'none', border: 'none', cursor: 'pointer', opacity: n <= surveyRating ? 1 : 0.25, transition: 'opacity .15s, transform .15s', transform: n <= surveyRating ? 'scale(1.15)' : 'scale(1)' }}>
                                
                              </button>
                            ))}
                          </div>
                        </div>
                        <div style={{ marginBottom: 20 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: C.inkMid, marginBottom: 8 }}>{t('home_any_comments')}</p>
                          <textarea className="pv-input" placeholder={t('home_feedback_placeholder')}
                            value={surveyComment} onChange={e => setSurveyComment(e.target.value)}
                            style={{ ...inputSt, height: 80, resize: 'vertical' }} />
                        </div>
                        <button onClick={handleSurveySubmit} disabled={surveySaving || surveyRating < 1}
                          style={{ background: surveyRating < 1 ? C.border : C.teal, color: surveyRating < 1 ? C.inkLt : 'white', border: 'none', borderRadius: 14, padding: '14px 28px', fontSize: 15, fontWeight: 700, cursor: surveyRating < 1 ? 'not-allowed' : 'pointer', transition: 'background .2s' }}>
                          {surveySaving ? t('home_submitting') : t('home_submit_feedback')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* MY HEALTH */}
            {activeTab === 'My Health' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('nav_my_health')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}><em>{t('health_overview_heading')}</em></h1>
                </div>

                {painReduced > 0 && (
                  <div style={{ background: C.tealLight, border: `1.5px solid ${C.tealMid}`, borderRadius: 20, padding: '22px 24px', display: 'flex', alignItems: 'center', gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.teal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon d={IC.trend} size={22} color="white" />
                    </div>
                    <div>
                      <div style={{ fontFamily: DISPLAY, fontSize: 26, color: C.tealDark, lineHeight: 1 }}>{t('health_pain_down')} {painReduced}%</div>
                      <div style={{ fontSize: 14, color: C.inkLt, marginTop: 4 }}>{t('health_since_first')}</div>
                    </div>
                  </div>
                )}

                <div style={{ background: C.white, borderRadius: 20, padding: '28px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('health_pain_chart')}</p>
                  <p style={{ fontSize: 14, color: C.inkLt, marginBottom: 20 }}>{t('health_lower_better')}</p>
                  {chartData.length > 1 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={chartData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.tealLight} />
                        <XAxis dataKey="s" tick={{ fontSize: 12, fill: C.inkLt, fontFamily: BODY }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: C.inkLt, fontFamily: BODY }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ borderRadius: 12, border: `1.5px solid rgba(13,148,136,0.2)`, fontSize: 14, fontFamily: BODY, background: 'white' }} />
                        <Line type="monotone" dataKey="Pain" stroke={C.teal} strokeWidth={2.5} dot={{ r: 4, fill: C.teal, strokeWidth: 0 }} activeDot={{ r: 6, fill: C.tealDark }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ padding: '40px', textAlign: 'center', background: C.cream, borderRadius: 14, border: `1.5px dashed ${C.border}` }}>
                      <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 16, color: C.inkLt }}>{t('health_no_data')}</div>
                    </div>
                  )}
                </div>

                {sessions.filter(s => s.notes || s.soap_subjective).length > 0 && (
                  <div style={{ background: C.white, borderRadius: 20, padding: '28px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 20 }}>{t('health_session_notes')}</p>
                    {sessions.slice().reverse().filter(s => s.notes || s.soap_subjective).map((s, i, arr) => (
                      <div key={s.id} style={{ padding: '18px 0', borderBottom: i < arr.length - 1 ? `1.5px solid rgba(13,148,136,0.2)` : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: C.tealDark, background: C.tealLight, padding: '4px 11px', borderRadius: 99, border: `1px solid ${C.tealMid}` }}>{t('health_session_label')} {s.session_number}</span>
                          <span style={{ fontSize: 12, color: C.inkLt, fontFamily: MONO }}>{s.date}</span>
                        </div>
                        <p style={{ fontSize: 15, color: C.inkMid, lineHeight: 1.8, fontFamily: DISPLAY, fontStyle: 'italic' }}>{s.notes || s.soap_subjective}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* EXERCISES */}
            {activeTab === 'Exercises' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('nav_exercises')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}><em>{t('exer_home_program')}</em></h1>
                </div>

                {totalExercises > 0 && <TodayProgress loggedCount={loggedTodayCount} total={totalExercises} />}

                {Object.keys(weekGroups).length === 0 && (
                  <div style={{ padding: '52px 28px', textAlign: 'center', background: C.cream, borderRadius: 20, border: `1.5px dashed ${C.border}` }}>
                    <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 16, color: C.inkLt }}>{t('exer_no_exercises')}</div>
                  </div>
                )}

                {Object.keys(weekGroups).sort().map(week => (
                  <div key={week}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '4px 0 14px' }}>
                      <div style={{ flex: 1, height: 1, background: C.border }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{week}</span>
                      <div style={{ flex: 1, height: 1, background: C.border }} />
                    </div>
                    <div className="grid-2" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {weekGroups[week].map(ex => {
                        const logged     = isLoggedToday(ex.id)
                        const todayLog   = exerciseLogs.find(l => l.exercise_id === ex.id && l.log_date === today)
                        const recentLogs = exerciseLogs.filter(l => l.exercise_id === ex.id).slice(0, 3)
                        return (
                          <div key={ex.id} className="lift" style={{ background: logged ? C.tealLight : C.white, borderRadius: 18, padding: '20px 22px', border: `1.5px solid ${logged ? C.tealMid : C.border}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                              <div>
                                <p style={{ fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 3, fontFamily: DISPLAY }}>{ex.exercise_name}</p>
                                <p style={{ fontSize: 13, color: C.inkLt, fontFamily: MONO }}>{ex.sets} {t('exer_sets')} × {ex.reps} {t('exer_reps')}</p>
                              </div>
                              {logged
                                ? <span style={{ fontSize: 12, fontWeight: 700, color: C.success, background: '#dcfce7', padding: '5px 12px', borderRadius: 99, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                    <Icon d={IC.check} size={12} color={C.success} /> {t('exer_done_badge')}
                                  </span>
                                : <button onClick={() => setLogModal(ex)} className="log-btn"
                                    style={{ padding: '8px 18px', background: C.teal, color: 'white', border: 'none', borderRadius: 99, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: BODY, flexShrink: 0 }}>
                                    {t('exer_log_it')}
                                  </button>
                              }
                            </div>
                            {ex.notes && <p style={{ fontSize: 14, color: C.inkMid, marginBottom: 10, lineHeight: 1.6 }}>{ex.notes}</p>}
                            <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', marginBottom: ex.video_url ? 6 : 0 }}>
                              {ex.video_url && (
                                <a href={ex.video_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#dbeafe', color: '#1d4ed8', borderRadius: 99, fontSize: 13, fontWeight: 600, textDecoration: 'none', border: '1px solid #bfdbfe' }}>
                                  <Icon d={IC.play} size={11} color="#1d4ed8" /> {t('exer_watch_video')}
                                </a>
                              )}
                              <button
                                onClick={() => openCaleb({ type: 'exercise', patientId: user.patient_id, patientName: patient?.name, exerciseName: ex.exercise_name, extra: { exerciseName: ex.exercise_name } })}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#f0fdfa', color: C.teal, borderRadius: 99, fontSize: 12, fontWeight: 600, border: `1px solid ${C.tealMid}`, cursor: 'pointer', fontFamily: BODY }}
                              >
                                <span style={{ fontSize: '10px' }}></span> {t('exer_ask_caleb')}
                              </button>
                            </div>
                            {logged && todayLog && (
                              <p style={{ fontSize: 13, color: C.teal, fontWeight: 600 }}>
                                {t('exer_difficulty')}: {todayLog.difficulty}/5
                                {todayLog.notes && <span style={{ color: C.inkLt, fontWeight: 400, marginLeft: 8 }}>· {todayLog.notes}</span>}
                              </p>
                            )}
                            {recentLogs.length > 0 && (
                              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1.5px solid rgba(13,148,136,0.2)` }}>
                                <p style={{ fontSize: 10, color: C.inkLt, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('exer_recent_logs')}</p>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {recentLogs.map(log => (
                                    <span key={log.id} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 99, background: C.cream, color: C.inkLt, border: `1.5px solid rgba(13,148,136,0.2)`, fontFamily: MONO }}>
                                      {log.log_date} · {log.difficulty}/5
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* GOALS */}
            {activeTab === 'Goals' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('nav_goals')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}><em>{t('goals_milestones')}</em></h1>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, background: C.border, borderRadius: 20, overflow: 'hidden' }}>
                  <div style={{ background: C.white, padding: '24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 38, color: C.teal, lineHeight: 1 }}>{completedGoals}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: 6 }}>{t('goals_completed_label')}</div>
                  </div>
                  <div style={{ background: `linear-gradient(135deg, ${C.tealDeep}, ${C.tealDark})`, padding: '24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 38, color: 'white', lineHeight: 1 }}>{goals.length - completedGoals}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: 6 }}>{t('goals_in_progress')}</div>
                  </div>
                </div>

                {goals.length === 0 && (
                  <div style={{ padding: '52px 28px', textAlign: 'center', background: C.cream, borderRadius: 20, border: `1.5px dashed ${C.border}` }}>
                    <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 16, color: C.inkLt }}>{t('goals_no_goals')}</div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {goals.map(g => (
                    <div key={g.id} className="lift" style={{ background: g.is_complete ? C.tealLight : C.white, borderRadius: 16, padding: '18px 22px', border: `1.5px solid ${g.is_complete ? C.tealMid : C.border}`, display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${g.is_complete ? C.teal : C.border}`, background: g.is_complete ? C.teal : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {g.is_complete && <Icon d={IC.check} size={13} color="white" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 15, fontWeight: g.is_complete ? 400 : 600, color: g.is_complete ? C.inkLt : C.ink, textDecoration: g.is_complete ? 'line-through' : 'none' }}>{g.goal_text}</p>
                        {g.target_value && <p style={{ fontSize: 13, color: C.inkLt, marginTop: 2 }}>{t('goals_target')} {g.target_value}</p>}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: g.is_complete ? C.tealDark : C.inkLt, background: g.is_complete ? C.tealFoam : C.cream, padding: '4px 12px', borderRadius: 99, border: `1px solid ${g.is_complete ? C.tealMid : C.border}`, whiteSpace: 'nowrap' }}>
                        {g.is_complete ? t('goals_done_badge') : t('goals_in_progress')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* INJURY EXPLORER — rendered full-width above pv-content, not here */}

            {/* MESSAGES */}
            {activeTab === 'Messages' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('nav_messages')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}><em>{t('msg_your_therapist')}</em></h1>
                </div>
                <div style={{ background: C.tealLight, border: `1.5px solid ${C.tealMid}`, borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon d={IC.msg} size={15} color={C.teal} />
                  <p style={{ fontSize: 14, color: C.tealDark, fontWeight: 500 }}>{t('msg_private_secure')}</p>
                </div>
                <Messages patientId={user.patient_id} senderRole="patient" senderName={patient.name} />
              </div>
            )}

            {/* APPOINTMENTS */}
            {activeTab === 'Appointments' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('nav_appointments')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}><em>{t('appt_schedule_heading')}</em></h1>
                </div>

                <div className="grid-2" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ background: C.white, borderRadius: 22, padding: '28px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 20 }}>{t('appt_request')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div>
                        <label style={labelSt}>{t('appt_date')}</label>
                        <input type="date" value={apptForm.appointment_date}
                          onChange={e => setApptForm({ ...apptForm, appointment_date: e.target.value })} style={inputSt} />
                      </div>
                      <div>
                        <label style={labelSt}>{t('appt_preferred_time')}</label>
                        <select value={apptForm.appointment_time}
                          onChange={e => setApptForm({ ...apptForm, appointment_time: e.target.value })} style={inputSt}>
                          <option value="">{t('appt_select_time')}</option>
                          {['8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM','11:00 AM','11:30 AM','1:00 PM','1:30 PM','2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelSt}>{t('appt_notes')}</label>
                        <input placeholder={t('appt_notes_placeholder')}
                          value={apptForm.notes} onChange={e => setApptForm({ ...apptForm, notes: e.target.value })} style={inputSt} />
                      </div>
                      {apptMessage && (
                        <div style={{ padding: '13px 16px', borderRadius: 12, fontSize: 14, fontWeight: 500, background: apptMessage.includes('sent') ? C.tealLight : C.amber, color: apptMessage.includes('sent') ? C.tealDark : '#92400e', border: `1px solid ${apptMessage.includes('sent') ? C.tealMid : '#fde68a'}` }}>
                          {apptMessage}
                        </div>
                      )}
                      <button className="btn-teal"
                        onClick={() => {
                          if (!apptForm.appointment_date || !apptForm.appointment_time) { setApptMessage(t('appt_select_date_time')); return }
                          axios.post(`${API}/appointments`, { ...apptForm, patient_id: user.patient_id })
                            .then(() => {
                              setApptMessage(t('appt_sent_msg'))
                              setApptForm({ appointment_date: '', appointment_time: '', notes: '' })
                              axios.get(`${API}/patients/${user.patient_id}/appointments`).then(r => setAppointments(r.data))
                            })
                            .catch(() => setApptMessage(t('appt_error_msg')))
                        }}
                        style={{ padding: '14px', background: C.teal, color: 'white', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: BODY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        {t('appt_send')} <Icon d={IC.arrow} size={15} color="white" />
                      </button>
                    </div>
                  </div>

                  {appointments.length > 0 && (
                    <div>
                      <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>{t('appt_your_appointments')}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {appointments.map(a => {
                          const sc = a.status === 'confirmed' ? C.tealDark : a.status === 'cancelled' ? C.red : C.warn
                          const sb = a.status === 'confirmed' ? C.tealLight : a.status === 'cancelled' ? '#fee2e2' : C.amber
                          const sl = a.status === 'confirmed' ? t('appt_status_confirmed') : a.status === 'cancelled' ? t('appt_status_cancelled') : t('appt_status_pending')
                          return (
                            <div key={a.id} className="lift" style={{ background: C.white, borderRadius: 16, padding: '16px 20px', border: `1.5px solid rgba(13,148,136,0.25)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <p style={{ fontSize: 15, fontWeight: 700, color: C.ink, fontFamily: DISPLAY }}>{a.appointment_date}</p>
                                <p style={{ fontSize: 13, color: C.inkLt, fontFamily: MONO, marginTop: 2 }}>{a.appointment_time}</p>
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, color: sc, background: sb, padding: '5px 12px', borderRadius: 99 }}>{sl}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BILLS */}
            {activeTab === 'Bills' && (
              <PatientBilling patientId={user.patient_id} />
            )}

            {/* ACCOUNT */}
            {activeTab === 'Account' && (
              <div className="pv-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{t('nav_account')}</p>
                  <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}><em>{t('acct_settings_heading')}</em></h1>
                </div>

                {/* Profile info */}
                <div style={{ background: C.white, borderRadius: 20, padding: '24px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>{t('acct_profile')}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: DISPLAY, fontSize: 20, color: 'white', fontStyle: 'italic' }}>{initials}</span>
                    </div>
                    <div>
                      <p style={{ fontSize: 17, fontWeight: 700, color: C.ink, fontFamily: DISPLAY }}>{patient.name}</p>
                      <p style={{ fontSize: 13, color: C.inkLt, fontFamily: MONO, marginTop: 2 }}>{patient.patient_email || user.email || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Change password */}
                <div style={{ background: C.white, borderRadius: 20, padding: '28px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>{t('acct_change_password')}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
                    <div>
                      <label style={labelSt}>{t('acct_current_pw')}</label>
                      <input className="pv-input" type="password" placeholder={t('acct_current_pw')}
                        value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                        style={inputSt} />
                    </div>
                    <div>
                      <label style={labelSt}>{t('acct_new_pw')}</label>
                      <input className="pv-input" type="password" placeholder={t('acct_at_least_8')}
                        value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                        style={inputSt} />
                    </div>
                    <div>
                      <label style={labelSt}>{t('acct_confirm_pw')}</label>
                      <input className="pv-input" type="password" placeholder={t('acct_repeat_pw')}
                        value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                        style={inputSt} />
                    </div>
                    {pwMsg && (
                      <div style={{ background: pwMsg.ok ? C.tealLight : '#fee2e2', border: `1px solid ${pwMsg.ok ? C.tealMid : '#fca5a5'}`, borderRadius: 10, padding: '10px 14px', fontSize: 14, color: pwMsg.ok ? C.tealDark : C.red, fontWeight: 600 }}>
                        {pwMsg.ok ? ' ' : ' '}{pwMsg.text}
                      </div>
                    )}
                    <button onClick={handlePasswordChange} disabled={pwSaving}
                      style={{ background: C.teal, color: 'white', border: 'none', borderRadius: 14, padding: '14px 28px', fontSize: 15, fontWeight: 700, cursor: pwSaving ? 'not-allowed' : 'pointer', opacity: pwSaving ? 0.7 : 1, alignSelf: 'flex-start', transition: 'opacity .2s' }}>
                      {pwSaving ? t('acct_saving') : t('acct_update_pw')}
                    </button>
                  </div>
                </div>

                {/* Sign out */}
                <div style={{ background: C.white, borderRadius: 20, padding: '24px', border: `1.5px solid rgba(13,148,136,0.25)` }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>{t('acct_session_label')}</p>
                  <button onClick={onLogout}
                    style={{ background: '#fee2e2', color: C.red, border: `1.5px solid #fca5a5`, borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    {t('acct_sign_out')}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  )
}